import { useState, useEffect, useRef } from 'react'
import { FileText, File, Mic, Calendar, Lightbulb, FlaskConical, Loader2, BookOpen, CheckSquare, Briefcase, Folder, ChevronRight } from 'lucide-react'
import { searchDocuments, searchFolders, fetchWithAuth } from '@/lib/api'
import { filterOpenTabIdsForSearch, filterUuidIdsForSearch } from '@/lib/open-tab-ids-for-search'
import {
  isFolderLikeMentionQuery,
  normalizeMentionSearchQuery,
} from '@/components/ui/contenteditable-chat-input.utils'
import { cn } from '@/lib/utils'
import type { DocumentReference } from '@/components/layout/chat-pane.types'

interface MentionAutocompleteProps {
  query: string
  position: { top: number; left: number }
  onSelect: (reference: DocumentReference) => void
  onClose: () => void
  showCategories?: boolean
  openTabIds?: string[]
  recentlyViewedIds?: string[]
  currentProjectId?: string | null
  /** Content-root prefixes from chat scope picker (same as chat `workspaceScope`). When set, @ results are limited to these trees. */
  folderPaths?: string[]
}

type DocumentType =
  | 'work_spec'
  | 'note'
  | 'voice_memo'
  | 'meeting'
  | 'idea'
  | 'research'
  | 'todo'
  | 'folder'
  | 'quiz'

const getDocumentIcon = (type: DocumentType) => {
  switch (type) {
    case 'folder':
      return Folder
    case 'work_spec':
      return FileText
    case 'voice_memo':
      return Mic
    case 'meeting':
      return Calendar
    case 'idea':
      return Lightbulb
    case 'research':
      return FlaskConical
    case 'todo':
      return FileText
    default:
      return File
  }
}

const getDocumentColor = (type: DocumentType) => {
  switch (type) {
    case 'folder':
      return 'text-blue-600 dark:text-blue-400'
    case 'work_spec':
      return 'text-blue-600 dark:text-blue-400'
    case 'voice_memo':
      return 'text-purple-600 dark:text-purple-400'
    case 'meeting':
      return 'text-green-600 dark:text-green-400'
    case 'idea':
      return 'text-yellow-600 dark:text-yellow-400'
    case 'research':
      return 'text-pink-600 dark:text-pink-400'
    case 'todo':
      return 'text-orange-600 dark:text-orange-400'
    default:
      return 'text-gray-600 dark:text-gray-400'
  }
}

const categories = [
  { type: 'category', label: 'Files & Folders', icon: FileText, filter: 'files' },
  { type: 'category', label: 'Docs', icon: BookOpen, filter: 'docs' },
  { type: 'category', label: 'Todos', icon: CheckSquare, filter: 'todos' },
  { type: 'category', label: 'Clients', icon: Briefcase, filter: 'clients' },
  { type: 'category', label: 'Projects', icon: Folder, filter: 'projects' },
]

// Stable empty array to prevent re-renders
const EMPTY_ARRAY: string[] = []

function normalizeRelPath(p: string) {
  return p.replace(/^\/+/, '').replace(/\/+$/, '')
}

/** True when `filePath` is under one of the workspace content roots (e.g. `docs/...`, `hop-studios/...`). */
export function isPathUnderFolderScopes(filePath: string | undefined | null, folderPaths: string[]): boolean {
  if (!folderPaths.length) return true
  if (!filePath) return false
  const fp = normalizeRelPath(filePath)
  return folderPaths.some((root) => {
    const r = normalizeRelPath(root)
    if (!r) return true
    return fp === r || fp.startsWith(`${r}/`)
  })
}

export function MentionAutocomplete({ 
  query, 
  position, 
  onSelect, 
  onClose,
  showCategories = true,
  openTabIds,
  recentlyViewedIds,
  currentProjectId,
  folderPaths = EMPTY_ARRAY,
}: MentionAutocompleteProps) {
  const [results, setResults] = useState<DocumentReference[]>([])
  const [folderResults, setFolderResults] = useState<DocumentReference[]>([])
  const [suggestedItems, setSuggestedItems] = useState<DocumentReference[]>([])
  const [loading, setLoading] = useState(false)
  const [foldersLoading, setFoldersLoading] = useState(false)
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const prevDepsRef = useRef<{ query: string; openTabIds: string; projectId: string | null; scopeKey: string }>({
    query: '',
    openTabIds: '',
    projectId: null,
    scopeKey: '',
  })

  // Use stable empty array if not provided
  const stableOpenTabIds = openTabIds || EMPTY_ARRAY
  const openTabIdsForSearch = filterOpenTabIdsForSearch(stableOpenTabIds) ?? []
  const stableRecentlyViewedIds = recentlyViewedIds || EMPTY_ARRAY
  const recentlyViewedIdsForSearch = filterUuidIdsForSearch(stableRecentlyViewedIds) ?? []
  const openTabIdsString = stableOpenTabIds.join(',')
  const recentlyViewedIdsString = stableRecentlyViewedIds.join(',')
  const projectIdString = currentProjectId ? String(currentProjectId) : ''
  const scopeKey = folderPaths.join(',')

  // Fetch suggested items when query is empty
  useEffect(() => {
    // Only fetch if dependencies actually changed
    const depsChanged = 
      prevDepsRef.current.query !== query ||
      prevDepsRef.current.openTabIds !== openTabIdsString ||
      prevDepsRef.current.projectId !== projectIdString ||
      prevDepsRef.current.scopeKey !== scopeKey

    if (query.trim() !== '') {
      if (depsChanged) {
        setSuggestedItems([])
        prevDepsRef.current = { query, openTabIds: openTabIdsString, projectId: projectIdString, scopeKey }
      }
      return
    }

    // Only fetch if dependencies changed
    if (!depsChanged) {
      return
    }

    prevDepsRef.current = { query, openTabIds: openTabIdsString, projectId: projectIdString, scopeKey }

    const fetchSuggestions = async () => {
      setSuggestionsLoading(true)
      try {
        const params = new URLSearchParams({ limit: '8' })
        if (stableOpenTabIds.length > 0) {
          params.append('openTabs', openTabIdsString)
        }
        if (currentProjectId) {
          params.append('currentProject', projectIdString)
        }

        const response = await fetchWithAuth(`/api/suggestions?${params}`)
        const data = await response.json()

        if (data.success && data.suggestions) {
          const documents: DocumentReference[] = data.suggestions
            .map((s: any) => ({
              id: s.documentId || s.id,
              title: s.title,
              type: s.type as DocumentType,
              path: s.filePath || s.path,
              excerpt: s.excerpt || s.reasoning
            }))
            .filter((d: DocumentReference) => isPathUnderFolderScopes(d.path, folderPaths))
          setSuggestedItems(documents)
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error)
        setSuggestedItems([])
      } finally {
        setSuggestionsLoading(false)
      }
    }

    fetchSuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, scopeKey])

  // Search documents and folders when query changes (debounced, abort stale requests)
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      setFolderResults([])
      setLoading(false)
      setFoldersLoading(false)
      return
    }

    const abortController = new AbortController()
    const trimmed = query.trim()
    const folderLike = isFolderLikeMentionQuery(trimmed)
    const searchTerm = normalizeMentionSearchQuery(trimmed)
    const docLimit = folderLike ? 4 : 8
    const folderLimit = folderLike ? 10 : 5

    const runDocumentSearch = async () => {
      if (folderLike && !searchTerm) {
        setResults([])
        return
      }
      setLoading(true)
      try {
        const docResponse = await searchDocuments(
          {
            query: searchTerm,
            limit: docLimit,
            openTabIds: openTabIdsForSearch.length > 0 ? openTabIdsForSearch : undefined,
            recentlyViewedIds:
              recentlyViewedIdsForSearch.length > 0 ? recentlyViewedIdsForSearch : undefined,
            ...(folderPaths.length > 0 ? { folderPaths } : {}),
          },
          { signal: abortController.signal },
        )

        if (abortController.signal.aborted) return

        const documents: DocumentReference[] = (docResponse.results || [])
          .map((doc: any) => ({
            id: doc.id,
            title: doc.title,
            type: doc.type as DocumentType,
            path: doc.filePath,
            excerpt: doc.excerpt,
            kind: 'document' as const,
          }))
          .filter((d: DocumentReference) => isPathUnderFolderScopes(d.path, folderPaths))

        setResults(documents)
        setSelectedIndex(0)
      } catch (error) {
        if (abortController.signal.aborted) return
        console.error('Mention document search failed:', error)
        setResults([])
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    const runFolderSearch = async () => {
      setFoldersLoading(true)
      try {
        const folderResponse = await searchFolders(
          { query: searchTerm, limit: folderLimit },
          { signal: abortController.signal },
        )

        if (abortController.signal.aborted) return

        const folders: DocumentReference[] = (folderResponse.folders || [])
          .map((folder: any) => ({
            id: folder.path,
            title: folder.name,
            type: 'folder' as DocumentType,
            path: folder.path,
            kind: 'folder' as const,
            documentCount: folder.documentCount,
          }))
          .filter((f: DocumentReference) => isPathUnderFolderScopes(f.path, folderPaths))

        setFolderResults(folders)
      } catch (error) {
        if (abortController.signal.aborted) return
        console.error('Mention folder search failed:', error)
        setFolderResults([])
      } finally {
        if (!abortController.signal.aborted) {
          setFoldersLoading(false)
        }
      }
    }

    const timeoutId = setTimeout(() => {
      void runDocumentSearch()
      void runFolderSearch()
    }, 400)

    return () => {
      clearTimeout(timeoutId)
      abortController.abort()
    }
  }, [query, openTabIdsString, recentlyViewedIdsString, scopeKey])

  // Combine items: search results when querying, suggested items + categories when empty
  // When querying, show folders first, then documents
  const displayItems = query.trim() ? [...folderResults, ...results] : suggestedItems
  const allItems = query.trim() 
    ? [...folderResults, ...results]  // Show folders first, then documents when querying
    : [...suggestedItems, ...(showCategories ? categories : [])]  // Show suggestions + categories when empty

  // Reset selected index when switching between query/no-query
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = allItems[selectedIndex]
        if (item) {
          if ('type' in item && item.type === 'category') {
            // Handle category selection (insert @category/ or filter)
            // For now, just close - can be enhanced later
            onClose()
          } else {
            onSelect(item as DocumentReference)
          }
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [allItems, selectedIndex, onSelect, onClose])

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedIndex])

  // Don't show if no query and no suggestions/categories
  if (!query.trim() && !suggestionsLoading && suggestedItems.length === 0 && (!showCategories || categories.length === 0)) {
    return null
  }

  const hasItems = displayItems.length > 0
  const isSearching = Boolean(query.trim()) && (loading || foldersLoading)
  const showBlockingLoader =
    suggestionsLoading || (isSearching && !hasItems)
  const showResults = query.trim()
    ? isFolderLikeMentionQuery(query.trim())
      ? 'Folders'
      : 'Results'
    : 'Suggested:'

  return (
    <div
      className="fixed z-[9999] w-[420px] overflow-hidden rounded-lg border border-border bg-popover shadow-2xl transition-opacity duration-200"
      data-autocomplete-menu="mention"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        // Always position above, growing upwards
        transform: 'translateY(-100%)',
        marginBottom: '8px', // Space from @ symbol line
        minHeight: showBlockingLoader || hasItems ? 'auto' : '60px' // Minimum height to prevent "just borders" issue
      }}
    >
      {/* Header - always show */}
      <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b border-border">
        {showResults}
      </div>

      {/* Loading state - show immediately when loading starts */}
      {showBlockingLoader && (
        <div className="flex items-center justify-center py-3 px-4 min-h-[60px]">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            {query.trim() ? 'Searching...' : 'Loading suggestions...'}
          </span>
        </div>
      )}

      {/* Results - show as soon as either request returns (do not wait for both) */}
      {!showBlockingLoader && hasItems && (
        <div
          ref={listRef}
          className="max-h-[300px] overflow-y-auto"
        >
          {displayItems.map((doc, index) => {
            const isFolder = doc.kind === 'folder' || doc.type === 'folder'
            const Icon = isFolder ? Folder : getDocumentIcon(doc.type)
            const colorClass = isFolder ? 'text-blue-600 dark:text-blue-400' : getDocumentColor(doc.type)
            const isSelected = index === selectedIndex

            return (
              <button
                key={doc.id}
                type="button"
                onMouseDown={(e) => {
                  // Keep focus in the composer so @mention text + selection survive the click.
                  e.preventDefault()
                }}
                onClick={() => onSelect(doc)}
                className={cn(
                  "flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors",
                  isSelected
                    ? "bg-accent text-accent-foreground"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                <Icon className={cn("h-4 w-4 shrink-0", colorClass)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-[13px] leading-tight">{doc.title}</span>
                    {isFolder && doc.documentCount !== undefined && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        ({doc.documentCount} {doc.documentCount === 1 ? 'doc' : 'docs'})
                      </span>
                    )}
                  </div>
                  {doc.path && (
                    <div className="truncate text-xs text-muted-foreground leading-tight">
                      {isFolder ? doc.path : doc.path.split('/').pop()}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* No results message */}
      {query.trim() && !isSearching && results.length === 0 && folderResults.length === 0 && (
        <div className="py-3 px-4 text-sm text-muted-foreground text-center min-h-[60px] flex items-center justify-center">
          No documents or folders found for &quot;{query}&quot;
        </div>
      )}

      {/* Categories section */}
      {showCategories && !query.trim() && !showBlockingLoader && (
        <div className="border-t border-border py-1">
          {categories.map((category, index) => {
            const actualIndex = displayItems.length + index
            const CategoryIcon = category.icon
            return (
              <button
                key={category.label}
                onClick={() => {
                  // Handle category selection (insert @category/ or filter)
                  // For now, just close - can be enhanced later
                  onClose()
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-2.5 px-3 py-1.5 text-left text-sm transition-colors",
                  selectedIndex === actualIndex
                    ? "bg-accent text-accent-foreground"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                <div className="flex items-center gap-2.5">
                  <CategoryIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-[13px] leading-tight">{category.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

